import { useState, Fragment } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import CartaoErro from '../../componentes/CartaoErro'
import type { ItemPedido, Preco, Produto, SubPedido } from '../../db/tipos'
import {
  adicionarItem, atualizarItem, removerItem, interpretarQuantidade, totalProjetado,
} from '../../pedidos/carrinho'
import { MOTIVOS_AJUSTE, MOTIVOS_ACRESCIMO, MOTIVO_CORRECAO } from '../../pedidos/ajustes'
import { faltaParaOMinimo } from '../../pedidos/fechamento'
import { calcularTotalItem, SEM_AVULSAS } from '../../pedidos/precos'
import {
  tabelaDoItem, avaliarQuantidade, avisoDaRegra, regraDoPreco,
} from '../../pedidos/regraQuantidade'
import { formatarMoeda } from '../../lib/formatos'

interface Props {
  subPedido: SubPedido
  aoFechar: () => void
}

// O que esta pendente de motivo: uma mudanca ja escolhida, esperando o porque.
type Pendente =
  | { tipo: 'quantidade'; item: ItemPedido; nova: number }
  | { tipo: 'remover'; item: ItemPedido }

export default function AjustarItens({ subPedido, aoFechar }: Props) {
  const [quantidades, setQuantidades] = useState<Record<string, string>>({})
  const [pendente, setPendente] = useState<Pendente | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [mudou, setMudou] = useState(false)
  // Recusa de valor digitado: antes deste conserto a tela devolvia em silencio.
  const [aviso, setAviso] = useState<{ nome: string; texto: string } | null>(null)
  const [cortarMesmoAssim, setCortarMesmoAssim] = useState(false)
  // Acrescentar produto depois do envio (2026-07-29): ela cortou, viu o aviso de
  // minimo e nao teve como completar o valor.
  const [acrescentando, setAcrescentando] = useState(false)
  const [buscaAcrescimo, setBuscaAcrescimo] = useState('')
  const [escolhido, setEscolhido] = useState<{ produto: Produto; preco: number } | null>(null)
  const [quantidadeAcrescimo, setQuantidadeAcrescimo] = useState('')
  const [motivoAcrescimo, setMotivoAcrescimo] = useState('')
  const [erroAcrescimo, setErroAcrescimo] = useState<string | null>(null)

  const itens = useLiveQuery(
    () =>
      banco.itens_pedido
        .where('sub_pedido_id')
        .equals(subPedido.id)
        .filter((i) => i.apagado_em === null)
        .toArray(),
    [subPedido.id],
  )
  const produtos = useLiveQuery(
    () => banco.produtos.where('representada_id').equals(subPedido.representada_id).toArray(),
    [subPedido.representada_id],
  )
  // O minimo e da MARCA, e uma marca pode ter varios sub-pedidos (um por
  // tabela): para saber se o corte derruba o minimo e preciso o pedido inteiro.
  const subPedidosDoPedido = useLiveQuery(
    () =>
      banco.sub_pedidos
        .where('pedido_id')
        .equals(subPedido.pedido_id)
        .filter((s) => s.apagado_em === null)
        .toArray(),
    [subPedido.pedido_id],
  )
  const representadas = useLiveQuery(() => banco.representadas.toArray(), [])
  // Para acrescentar: o cliente (do pedido) e os precos DA TABELA deste
  // sub-pedido -- acrescentar tem de respeitar a tabela pela qual o pedido foi
  // vendido, senao entraria preco de outra condicao no mesmo documento.
  const pedido = useLiveQuery(() => banco.pedidos.get(subPedido.pedido_id), [subPedido.pedido_id])
  const precosDaTabela = useLiveQuery(
    () =>
      subPedido.tabela_preco_id
        ? banco.precos
            .where('tabela_preco_id')
            .equals(subPedido.tabela_preco_id)
            .filter((p) => p.apagado_em === null)
            .toArray()
        : // anotar o tipo e obrigatorio: `Promise.resolve([])` infere never[] e
          // envenena a uniao do useLiveQuery (mesma armadilha de DetalhePedido).
          Promise.resolve<Preco[]>([]),
    [subPedido.tabela_preco_id],
  )
  const nomeProduto = (id: string) => produtos?.find((p) => p.id === id)?.descricao ?? id

  // Aumentar nao e corte da fabrica: so "correcao minha" faz sentido, e
  // restringir na origem evita metrica suja.
  const subindo = pendente?.tipo === 'quantidade' && pendente.nova > pendente.item.quantidade
  const opcoes = subindo
    ? MOTIVOS_AJUSTE.filter((m) => m.codigo === MOTIVO_CORRECAO)
    : MOTIVOS_AJUSTE

  // Cortar item pode derrubar a marca abaixo do pedido minimo da fabrica -- um
  // caminho novo que este ciclo criou e que nada checava. O aviso vem ANTES de
  // aplicar (decisao dela em 2026-07-29), do mesmo jeito que no fechamento.
  const faltaMinimo = (() => {
    if (!pendente || !itens || !subPedidosDoPedido || !representadas) return null
    const novaQuantidade = pendente.tipo === 'remover' ? 0 : pendente.nova
    return faltaParaOMinimo(
      subPedidosDoPedido,
      new Map(representadas.map((r) => [r.id, r])),
      subPedido.id,
      totalProjetado(itens, pendente.item.id, novaQuantidade),
    )
  })()
  const podeConfirmar = motivo !== '' && (faltaMinimo === null || cortarMesmoAssim)

  // Quanto falta AGORA (sem mudanca pendente): e a conta que ela quer ver
  // enquanto escolhe o produto para completar o minimo.
  const faltamAgora =
    subPedidosDoPedido && representadas
      ? faltaParaOMinimo(
          subPedidosDoPedido,
          new Map(representadas.map((r) => [r.id, r])),
          subPedido.id,
          subPedido.valor_total,
        )
      : null

  const termoAcrescimo = buscaAcrescimo.trim().toLowerCase()
  const candidatos =
    termoAcrescimo.length < 2
      ? []
      : (produtos ?? [])
          .filter((p) => p.ativo && p.apagado_em === null)
          .flatMap((produto) => {
            const preco = precosDaTabela?.find((pr) => pr.produto_id === produto.id)?.preco
            if (preco === undefined) return [] // sem preco nesta tabela: nao oferece
            const alvo = `${produto.codigo} ${produto.descricao}`.toLowerCase()
            return alvo.includes(termoAcrescimo) ? [{ produto, preco }] : []
          })
          .slice(0, 8)

  function limparAcrescimo() {
    setAcrescentando(false)
    setBuscaAcrescimo('')
    setEscolhido(null)
    setQuantidadeAcrescimo('')
    setMotivoAcrescimo('')
    setErroAcrescimo(null)
  }

  async function confirmarAcrescimo() {
    if (!escolhido || !pedido || !subPedido.tabela_preco_id || !motivoAcrescimo) return
    // Reusa a mesma leitura de quantidade do resto da tela (aceita virgula, e
    // nunca recusa em silencio). Aqui a atual e 0: nada existe ainda.
    const acao = interpretarQuantidade(quantidadeAcrescimo, 0)
    if (acao.acao !== 'quantidade') {
      setErroAcrescimo(
        acao.acao === 'remover' ? 'Digite uma quantidade maior que zero.' : acao.aviso,
      )
      return
    }
    setErroAcrescimo(null)
    try {
      const resultado = await adicionarItem(
        pedido.id,
        pedido.cliente_id,
        escolhido.produto,
        subPedido.tabela_preco_id,
        acao.nova,
        // Acrescentar unidade solta pela tela de AJUSTE e a Task 7; aqui a
        // dimensao avulsa fica intocada de proposito.
        SEM_AVULSAS,
        motivoAcrescimo,
      )
      if (!resultado.sucesso) {
        setErroAcrescimo('Este produto não tem preço nesta tabela.')
        return
      }
      limparAcrescimo()
      setMudou(true)
    } catch (excecao) {
      setErroAcrescimo(excecao instanceof Error ? excecao.message : 'Falha ao acrescentar.')
    }
  }

  function abrirPendente(novo: Pendente) {
    setPendente(novo)
    setMotivo('')
    setAviso(null)
    setCortarMesmoAssim(false)
  }

  function cancelarPendente() {
    setPendente(null)
    setMotivo('')
    setErro(null)
    setCortarMesmoAssim(false)
  }

  async function confirmar() {
    if (!pendente || !motivo) return
    setErro(null)
    try {
      if (pendente.tipo === 'remover') await removerItem(pendente.item, motivo)
      else await atualizarItem(pendente.item, { quantidade: pendente.nova }, motivo)
      cancelarPendente()
      setMudou(true)
    } catch (excecao) {
      setErro(excecao instanceof Error ? excecao.message : 'Falha ao ajustar.')
    }
  }

  return (
    <div className="mt-2 space-y-2">
      <table className="text-xs w-full">
        <tbody>
          {(itens ?? []).map((item) => {
            // Reusa `precosDaTabela`, a consulta viva que a tela JA tem: o
            // sub-pedido e agrupado por marca + tabela, entao todo item de
            // preco de tabela daqui veio de `subPedido.tabela_preco_id`. Uma
            // segunda leitura de `banco.precos` seria duplicata que diverge.
            const tabelaId = tabelaDoItem(item.origem_preco)
            const precoDoItem = tabelaId
              ? (precosDaTabela ?? []).find(
                  (p) => p.tabela_preco_id === tabelaId && p.produto_id === item.produto_id,
                )
              : undefined
            // Avalia o que ESTA NO CAMPO, e nao o que esta gravado: aqui a
            // pessoa digita a quantidade nova e so confirma depois, entao o
            // aviso tem de acompanhar a digitacao. Este e o caso MAIS perigoso
            // da feature -- cortar quantidade num pedido ja fechado derruba o
            // item abaixo do minimo sem ninguem estar olhando para o preco.
            const quantidadeNoCampo = Number(
              (quantidades[item.id] ?? String(item.quantidade)).replace(',', '.'),
            )
            const avisoRegra = precoDoItem && Number.isFinite(quantidadeNoCampo)
              ? avisoDaRegra(avaliarQuantidade(quantidadeNoCampo, regraDoPreco(precoDoItem)))
              : null
            return (
            <Fragment key={item.id}>
            <tr className="border-t border-slate-100">
              <td className="py-1 text-slate-700">{nomeProduto(item.produto_id)}</td>
              <td className="py-1">
                <input
                  aria-label="Quantidade"
                  inputMode="numeric"
                  value={quantidades[item.id] ?? String(item.quantidade)}
                  onChange={(e) => {
                    setAviso(null)
                    setQuantidades((a) => ({ ...a, [item.id]: e.target.value }))
                  }}
                  className="w-16 border border-slate-300 rounded px-1 py-0.5"
                />
              </td>
              {/* Sem o valor unitario ela nao consegue conferir a conta: ve
                  "6 un" e "R$ 60,00" e nao tem como saber se esta certo.
                  O desconto aparece so quando existe -- sem ele a aritmetica
                  da linha nao fecharia, e o objetivo aqui e justamente poder
                  fechar a conta de cabeca. */}
              <td className="py-1 text-slate-500 whitespace-nowrap">
                × {formatarMoeda(item.preco_unitario)}
                {item.desconto_pct > 0 && (
                  <span className="text-amber-700"> −{item.desconto_pct}%</span>
                )}
              </td>
              <td className="py-1 text-slate-700 font-medium whitespace-nowrap">
                = {formatarMoeda(item.total_item)}
              </td>
              <td className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    // ZERO quer dizer "tira isso" -- e o gesto natural dela.
                    // Nenhum ramo daqui fica em silencio.
                    const acao = interpretarQuantidade(
                      quantidades[item.id] ?? String(item.quantidade),
                      item.quantidade,
                    )
                    if (acao.acao === 'recusar') {
                      setAviso({ nome: nomeProduto(item.produto_id), texto: acao.aviso })
                      setPendente(null)
                      return
                    }
                    abrirPendente(
                      acao.acao === 'remover'
                        ? { tipo: 'remover', item }
                        : { tipo: 'quantidade', item, nova: acao.nova },
                    )
                  }}
                  className="text-teal-700 underline"
                >
                  Aplicar
                </button>
              </td>
              <td className="py-1">
                <button
                  type="button"
                  aria-label={`Tirar ${nomeProduto(item.produto_id)}`}
                  onClick={() => abrirPendente({ tipo: 'remover', item })}
                  className="text-red-600"
                >
                  ✕
                </button>
              </td>
            </tr>
            {avisoRegra && (
              // Linha propria e nao dentro da celula: em 360px a celula do
              // nome ja e estreita, e a frase quebraria em quatro pedacos.
              // Ambar, e nao vermelho: e a condicao da fabrica, nao erro dela.
              <tr>
                <td colSpan={5} className="pb-1 text-amber-700">{avisoRegra}</td>
              </tr>
            )}
            </Fragment>
            )
          })}
        </tbody>
      </table>

      {aviso && (
        <p className="text-sm text-red-600">
          {aviso.nome}: {aviso.texto}
        </p>
      )}

      {pendente && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
          <p className="text-sm text-slate-700">
            {pendente.tipo === 'remover'
              ? `Tirando ${nomeProduto(pendente.item.produto_id)} (${pendente.item.quantidade} un, ${formatarMoeda(pendente.item.total_item)}). Por quê?`
              : // A conta do corte fica VISIVEL na hora de decidir: antes ela
                // via so "de 10 para 6 un" e confirmava sem poder conferir.
                `Mudando ${nomeProduto(pendente.item.produto_id)} de ${pendente.item.quantidade} para ${pendente.nova} un (${formatarMoeda(pendente.item.total_item)} → ${formatarMoeda(
                  calcularTotalItem(
                    pendente.nova,
                    pendente.item.preco_unitario,
                    pendente.item.desconto_pct,
                  ),
                )}). Por quê?`}
          </p>
          {opcoes.map((m) => (
            <label key={m.codigo} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="motivo-ajuste"
                checked={motivo === m.codigo}
                onChange={() => setMotivo(m.codigo)}
                aria-label={m.rotulo}
              />
              {m.rotulo}
            </label>
          ))}

          {faltaMinimo && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-2">
              <h4 className="text-sm font-medium text-amber-800">Pedido mínimo não atingido</h4>
              <p className="text-xs text-amber-700">
                {faltaMinimo.representadaNome}: faltam {formatarMoeda(faltaMinimo.faltam)}
              </p>
              <label className="flex items-center gap-2 text-sm text-amber-800">
                <input
                  type="checkbox"
                  checked={cortarMesmoAssim}
                  onChange={(e) => setCortarMesmoAssim(e.target.checked)}
                />
                Cortar mesmo assim (a fábrica pode recusar o pedido)
              </label>
            </div>
          )}

          {erro && <CartaoErro titulo="Não foi possível confirmar">{erro}</CartaoErro>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void confirmar()}
              disabled={!podeConfirmar}
              className="bg-teal-700 text-white rounded px-3 py-1 text-sm disabled:opacity-50"
            >
              Confirmar
            </button>
            <button
              type="button"
              onClick={cancelarPendente}
              className="text-slate-600 text-sm underline"
            >
              Voltar
            </button>
          </div>
        </div>
      )}

      {/* Acrescentar produto: nasceu do beco sem saida que ela achou no smoke --
          o aviso de pedido minimo aparecia e nao havia como completar o valor. */}
      {!pendente && subPedido.tabela_preco_id !== null && !acrescentando && (
        <button
          type="button"
          onClick={() => setAcrescentando(true)}
          className="text-teal-700 text-sm underline"
        >
          ＋ Acrescentar produto
        </button>
      )}

      {acrescentando && (
        <div className="bg-white border border-teal-200 rounded-xl p-3 space-y-2">
          {faltamAgora && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
              Faltam {formatarMoeda(faltamAgora.faltam)} para o mínimo de{' '}
              {faltamAgora.representadaNome}.
            </p>
          )}

          {!escolhido ? (
            <>
              <input
                aria-label="Buscar produto para acrescentar"
                placeholder="Buscar produto (mín. 2 letras)"
                value={buscaAcrescimo}
                onChange={(e) => setBuscaAcrescimo(e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
              />
              {termoAcrescimo.length >= 2 && candidatos.length === 0 && (
                <p className="text-xs text-slate-500">
                  Nenhum produto desta tabela com esse nome. Só entram produtos que têm preço na
                  tabela pela qual este pedido foi vendido.
                </p>
              )}
              <ul className="divide-y">
                {candidatos.map((c) => (
                  <li key={c.produto.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setEscolhido(c)
                        setQuantidadeAcrescimo('')
                        setMotivoAcrescimo('')
                        setErroAcrescimo(null)
                      }}
                      className="w-full text-left py-1 text-sm text-slate-700"
                    >
                      {c.produto.descricao} — {formatarMoeda(c.preco)}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-700">
                Acrescentando {escolhido.produto.descricao} ({formatarMoeda(escolhido.preco)} cada).
                Quantas unidades, e por quê?
              </p>
              <input
                aria-label="Quantidade a acrescentar"
                inputMode="numeric"
                value={quantidadeAcrescimo}
                onChange={(e) => {
                  setErroAcrescimo(null)
                  setQuantidadeAcrescimo(e.target.value)
                }}
                className="w-20 border border-slate-300 rounded px-2 py-1 text-sm text-right"
              />
              {/* Lista PROPRIA: motivo de corte fala de perda, e acrescentar nao
                  e perda -- e o que mantem a metrica limpa. */}
              {MOTIVOS_ACRESCIMO.map((m) => (
                <label key={m.codigo} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="motivo-acrescimo"
                    checked={motivoAcrescimo === m.codigo}
                    onChange={() => setMotivoAcrescimo(m.codigo)}
                    aria-label={m.rotulo}
                  />
                  {m.rotulo}
                </label>
              ))}
              {erroAcrescimo && <CartaoErro titulo="Não foi possível confirmar">{erroAcrescimo}</CartaoErro>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void confirmarAcrescimo()}
                  disabled={motivoAcrescimo === '' || quantidadeAcrescimo.trim() === ''}
                  className="bg-teal-700 text-white rounded px-3 py-1 text-sm disabled:opacity-50"
                >
                  Acrescentar
                </button>
                <button
                  type="button"
                  onClick={() => setEscolhido(null)}
                  className="text-slate-600 text-sm underline"
                >
                  Trocar produto
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={limparAcrescimo}
            className="text-slate-600 text-sm underline"
          >
            Fechar acréscimo
          </button>
        </div>
      )}

      {mudou && (
        <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
          Os itens mudaram. Reenvie o PDF ao cliente.
        </p>
      )}

      <button type="button" onClick={aoFechar} className="text-slate-600 text-sm underline">
        Fechar ajuste
      </button>
    </div>
  )
}
